import { diContainer, serviceNames } from '../diContainer/diContainer';
import SaveNewBillingAddressUseCase from './SaveNewBillingAddressUseCase';
import BillingRepository from '../repositories/BillingRepository';
import SubmitNewsletterUseCase from './SubmitNewsletterUseCase';
import { BILLING_FORM_ERROR, BILLING_FORM_ERROR_SELECTED, INVOICE_FORM_ERROR } from './ValidateAndSubmitAddressesUseCase';
import TransactionLoadHandler from './TransactionLoadHandler';
import Config from '../domain/entities/Config';
import AddressFormStore from '../domain/entities/AddressFormStore';
import Address from '../domain/entities/Address';
import { FIELD_TYPE } from '../constants/Constants';
import { IAddressSchema } from '../api/JsonSchemaApi';
import InvoiceRepository from '../repositories/InvoiceRepository';
import SaveInvoiceDetailsUseCase from './SaveInvoiceDetailsUseCase';

class SubmitConvenienceStoreUseCase extends TransactionLoadHandler {
  private billingRepository: BillingRepository;

  private invoiceRepository: InvoiceRepository;

  private config: Config;

  constructor() {
    super();
    this.billingRepository = diContainer.get(serviceNames.billingRepository);
    this.invoiceRepository = diContainer.get(serviceNames.invoiceRepository);
    this.config = diContainer.get(serviceNames.config);
  }

  public async run(): Promise<void> {
    const transaction = await this.transactionRepository.getTransaction();
    const { showInvoiceSection } = this.config;
    const { billingAddress, convenienceStore } = transaction;
    if (!convenienceStore.isReady) {
      return;
    }

    if (showInvoiceSection && !this.invoiceRepository.validateInvoiceDetails()) {
      throw new Error(INVOICE_FORM_ERROR);
    }

    await new SaveInvoiceDetailsUseCase().execute();

    if (billingAddress.isNewAddress) {
      if (!this.billingRepository.formStore.isValid) {
        this.billingRepository.formStore.setShowErrors(true);
        throw new Error(BILLING_FORM_ERROR);
      }

      const saveNewBillingAddressUseCase = new SaveNewBillingAddressUseCase();
      await saveNewBillingAddressUseCase.execute();

    } else {
      // validate selected billing address
      const validationSchema = await this.getBillingValidationSchema(billingAddress);

      const isBillingAddressValid = new AddressFormStore(this.config)
        .setAddress(billingAddress)
        .validateAddressBySchema(validationSchema);

      if (!isBillingAddressValid) {
        throw new Error(BILLING_FORM_ERROR_SELECTED);
      }
    }

    const submitNewsletterUseCase = new SubmitNewsletterUseCase();
    await submitNewsletterUseCase.execute();
  }

  private async getBillingValidationSchema(
    billingAddress: Address,
  ): Promise<IAddressSchema> {
    if (this.config.jsonSchemasEnabled) {
      return this.billingRepository.loadAddressSchema(
        billingAddress.attributes[FIELD_TYPE.COUNTRY_ID],
      );
    }

    return {
      properties: this.config.billingAddressInputSchema,
      required: [],
    } as IAddressSchema;
  }
}

export default SubmitConvenienceStoreUseCase;
